import { useRef, useState } from "react";
import {
  addLocalChange,
  ConnectionState,
  setConnected,
  setConnectionStatus,
} from "../../app/slices/WebSocketSlice";
import { useDispatch, useStore } from "react-redux";
import { WebSocketService } from "../../services/WebSocketService";
import {
  CreatorNetworkMessage,
  CreatorNetworkMessageType,
} from "../../models/CreatorNetworkMessage";
import {
  selectCurrentEnvironmentId,
  setEnvironmentMetadata,
} from "../../app/slices/EnvironmentSlice";
import { Environment } from "../../models/Environment";
import { PropMetadata } from "../../dto/PropMetadata";
import {
  selectUsedProps,
  updateUsedProp,
} from "../../app/slices/PropLibrarySlice";
import useAutoReImportDynamicInteractions from "./AutoReImportDynamicInteractions.hook";
import { syncPropPrefabNames } from "./PropPrefabNameSync";
import { RootState } from "../../app/Store";
import { SituationMetadata } from "../../dto/SituationMetadata";
import { setIsDirty } from "../../app/slices/TrainingSlice";
import {
  selectEnvironmentPropDetailsByPropId,
  syncEnvironmentPropPlacements,
  upsertEnvironmentPropDetails,
} from "../../app/slices/EnvironmentPropSlice";
import { EnvironmentPropDTO } from "../../dto/EnvironmentPropDTO";
import { PropDetailsDTO } from "../../dto/PropDetailsDTO";
import { PropService } from "../../services/PropService";
import { selectToken } from "../../app/slices/AuthenticationSlice";
import { selectCurrentOrganization } from "../../app/slices/UserSlice";

const ENVIRONMENT_PROP_TYPE = 0;

interface SyncEnvironmentPropsPayload {
  environmentId: string;
  placements: EnvironmentPropDTO[];
}

export function useCreatorWebSocketConnector() {
  const dispatch = useDispatch();
  const { reImportDynamicInteractions } = useAutoReImportDynamicInteractions();

  const getState = useStore().getState;

  const connect = () => {
    WebSocketService.onConnect(onConnect);
    WebSocketService.onMessage(onMessage);
    WebSocketService.onClose(onClose);
    WebSocketService.connect();
  };

  const disconnect = () => {
    WebSocketService.offConnect(onConnect);
    WebSocketService.offMessage(onMessage);
    WebSocketService.offClose(onClose);
    WebSocketService.disconnect();

    dispatch(setConnected(false));
  };

  const onClose = () => {
    dispatch(setConnected(false));
  };

  const onConnect = () => {
    dispatch(setConnected(true));
    const message: CreatorNetworkMessage = {
      messageType: CreatorNetworkMessageType.CreatorConnected,
      data: "",
    };
    WebSocketService.sendMessage(JSON.stringify(message));
  };

  const onMessage = (data: any) => {
    console.log(data);
    if (isValidJSON(data)) parseMessage(data);
  };

  const parseMessage = (message: string) => {
    const messageObject = JSON.parse(message);
    // we either entered or exit play mode in unity
    if (
      messageObject.messageType === CreatorNetworkMessageType.ReadyForScenario
    ) {
      const readyForScenario = messageObject.data === "True";
      dispatch(
        setConnectionStatus(
          readyForScenario
            ? ConnectionState.readyForScenario
            : ConnectionState.notReadyForScenario
        )
      );
    }

    // we have started playing the scenario in unity
    if (
      messageObject.messageType ===
      CreatorNetworkMessageType.StartPlayingScenario
    ) {
      dispatch(setConnectionStatus(ConnectionState.playingScenario));
    }

    // we either aborted the scenario or finished playing it which means we are ready for the next scenario
    if (
      messageObject.messageType ===
      CreatorNetworkMessageType.FinishPlayingScenario
    ) {
      dispatch(setConnectionStatus(ConnectionState.readyForScenario));
    }

    // syncing an environment from unity
    if (
      messageObject.messageType === CreatorNetworkMessageType.SyncEnvironment
    ) {
      handleSyncEnvironment(messageObject);
    }

    if (messageObject.messageType === CreatorNetworkMessageType.SyncProp) {
      handleSyncProp(messageObject);
    }

    if (messageObject.messageType === CreatorNetworkMessageType.SyncSituation) {
      handleSyncSituation(messageObject);
    }

    if (
      messageObject.messageType ===
      CreatorNetworkMessageType.SyncEnvironmentProps
    ) {
      handleSyncEnvironmentProps(messageObject);
    }
  };

  const handleSyncEnvironment = (networkMessage: CreatorNetworkMessage) => {
    try {
      const environment: Environment = JSON.parse(networkMessage.data);

      // set the environment metadata in the slice
      dispatch(
        setEnvironmentMetadata({
          take_positions: environment.take_positions ?? [],
          layers: environment.layers ?? [],
        })
      );

      // notify slice that we have unsaved local changes
      dispatch(
        addLocalChange({
          type: "Environment",
          metadata: networkMessage.data,
          id: environment.id,
          name: environment.name,
        })
      );

      // mark dirty
      dispatch(setIsDirty());
    } catch (error) {
      alert("Error parsing environment: " + error);
    }
  };

  const handleSyncProp = (networkMessage: CreatorNetworkMessage) => {
    try {
      const propMetadata: PropMetadata = JSON.parse(networkMessage.data);
      const state = getState() as RootState;
      const usedProps = "propLibrary" in state ? selectUsedProps(state) : {};
      const usedProp = usedProps[propMetadata.id];
      const enrichedPropMetadata: PropMetadata = {
        ...propMetadata,
        hideInPropLibrary:
          usedProp?.metadata.hideInPropLibrary ??
          usedProp?.propContent.hideInPropLibrary ??
          propMetadata.hideInPropLibrary ??
          false,
      };

      // update local changes slice
      dispatch(
        addLocalChange({
          type: "Prop",
          metadata: JSON.stringify(enrichedPropMetadata),
          id: enrichedPropMetadata.id,
          name: enrichedPropMetadata.name,
        })
      );
      // update used prop
      dispatch(updateUsedProp(enrichedPropMetadata));

      // update all dynamic interactions that use this prop
      reImportDynamicInteractions(
        enrichedPropMetadata,
        dispatch,
        getState as () => RootState
      );

      // update stale prefab names in presets and interactions
      syncPropPrefabNames(
        enrichedPropMetadata,
        dispatch,
        getState as () => RootState
      );

      // mark dirty
      dispatch(setIsDirty());
    } catch (error) {
      alert("Error parsing prop: " + error);
    }
  };

  const handleSyncSituation = (networkMessage: CreatorNetworkMessage) => {
    try {
      const situationMetadata: SituationMetadata = JSON.parse(
        networkMessage.data
      );

      // update local changes slice
      dispatch(
        addLocalChange({
          type: "Situation",
          metadata: networkMessage.data,
          id: situationMetadata.id,
          name: situationMetadata.name,
        })
      );

      // update used situations
      dispatch(updateUsedProp(situationMetadata));

      // re import dynamic interactions
      reImportDynamicInteractions(
        situationMetadata,
        dispatch,
        getState as () => RootState
      );

      // update stale prefab names in presets and interactions
      syncPropPrefabNames(
        situationMetadata,
        dispatch,
        getState as () => RootState
      );

      // mark dirty
      dispatch(setIsDirty());
    } catch (error) {
      alert("Error parsing situation: " + error);
    }
  };

  const handleSyncEnvironmentProps = async (
    networkMessage: CreatorNetworkMessage
  ) => {
    try {
      const payload: SyncEnvironmentPropsPayload = JSON.parse(
        networkMessage.data
      );
      const state = getState() as RootState;
      const currentEnvironmentId = selectCurrentEnvironmentId(state);

      if (payload.environmentId !== currentEnvironmentId) {
        console.warn(
          `Ignoring environment props sync for ${payload.environmentId}; current environment is ${currentEnvironmentId}.`
        );
        return;
      }

      dispatch(
        syncEnvironmentPropPlacements({
          environmentId: payload.environmentId,
          placements: payload.placements ?? [],
        })
      );

      dispatch(
        addLocalChange({
          type: "EnvironmentProps",
          metadata: JSON.stringify(payload.placements ?? []),
          id: `environment-props:${payload.environmentId}`,
          name: "Environment Props",
        })
      );

      dispatch(setIsDirty());

      const detailsByPropId = selectEnvironmentPropDetailsByPropId(state);
      const missingPropIds = [
        ...new Set(
          (payload.placements ?? [])
            .map((placement) => placement.propId)
            .filter((propId) => !detailsByPropId[propId])
        ),
      ];

      const token = selectToken(state);
      const org = selectCurrentOrganization(state);
      if (!token || !org || missingPropIds.length === 0) return;

      const details = (
        await Promise.all(
          missingPropIds.map((propId) =>
            PropService.GetPropDetails(
              org.id,
              token,
              ENVIRONMENT_PROP_TYPE,
              propId
            )
          )
        )
      ).filter((detail): detail is PropDetailsDTO => !!detail);

      if (details.length > 0) {
        dispatch(upsertEnvironmentPropDetails({ details }));
      }
    } catch (error) {
      alert("Error parsing environment props: " + error);
    }
  };

  const isValidJSON = (message: string) => {
    try {
      JSON.parse(message);
      return true;
    } catch (error) {
      return false;
    }
  };

  return { connect, disconnect };
}
